import { useMutation, useQueryClient } from '@tanstack/react-query';
import findIndex from 'lodash-es/findIndex';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { EDT_KEY } from '@teetsh/app/src/queries/edt/useEDTs';
import { EmploiDuTemps } from '@teetsh/app/src/types/edt';
import { addToEDTPast } from '@teetsh/app/src/queries/edt/historyAtoms';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

function orderSeancesBySeancesIdList(seances: any, seanceIds: string[]) {
  return seances.map(ev => {
    const matchingIndex = findIndex(seanceIds, id => ev.id === id);
    if (matchingIndex > -1) {
      return {
        ...ev,
        order: matchingIndex + 1,
      };
    }
    return ev;
  });
}

export default function useReorderEdtSeances(
  edtId: string,
  disableUndoRedo: boolean = false,
) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['emploiDuTemps']);
  const queryClient = useQueryClient();
  const requestURL = `/schoolyears/${schoolyear.id}/schools/${school.id}/emplois-du-temps`;
  const [, addToPast] = useAtom(addToEDTPast);
  const { mutateAsync } = useMutation({
    mutationFn: (seanceIds: string[]) =>
      request(`${requestURL}/${edtId}/seances/reorder`, {
        method: 'PATCH',
        body: {
          seanceIds,
        },
      }),
    onMutate: (seanceIds: string[]) => {
      if (!disableUndoRedo) {
        const oldEDT = queryClient.getQueryData<EmploiDuTemps>([
          schoolyear.id,
          school.id,
          EDT_KEY,
          edtId,
        ]);
        if (oldEDT) {
          addToPast({ edtId: oldEDT.id, seances: oldEDT.events });
        }
      }
      queryClient.setQueryData<EmploiDuTemps>(
        [schoolyear.id, school.id, EDT_KEY, edtId],
        (oldEDT: EmploiDuTemps) => {
          const oldEvents = oldEDT?.events || [];
          return {
            ...oldEDT,
            events: orderSeancesBySeancesIdList(oldEvents, seanceIds),
          };
        },
      );
    },
    onError: () => {
      notifyError({
        title: t('emploiDuTemps:messages.edt.updateError'),
      });
    },
  });
  return mutateAsync;
}
